import axios from 'axios';

import apiVersions from './apiVersions';

const getSubOrFullDomain = () =>
  window.location.hostname.match(/.*financetech(demo|test|365).com/g)
    ? window.location.hostname.split('.')[0]
    : window.location.hostname;

const getAPIBaseURL = () => {
  const hostnameParts = window.location.hostname.split('.');
  const subdomain = hostnameParts.shift();
  const subdomainParts = subdomain.split('-');
  const apiSubdomain =
    subdomainParts.length > 1 ? [subdomainParts[0], 'api'].join('-') : 'api';

  return 'https://' + [apiSubdomain, ...hostnameParts].join('.') + '/';
};

const normalizeBaseURL = (endpoint) =>
  endpoint ? endpoint.replace(/\/+$/, '') : endpoint;

const apiClient = (service, func, data = {}, options = {}) => {
  let { clientID, sessionID, ...body } = data;
  let {
    // eslint-disable-next-line no-console
    exceptionHandler = console.error,
    // eslint-disable-next-line no-console
    errorHandler = console.error,
    // eslint-disable-next-line no-console
    warningHandler = console.warn,
    resetUserState,
    ...config
  } = options;

  const subdomain = process.env.REACT_APP_SUBDOMAIN || getSubOrFullDomain();
  const baseURL = normalizeBaseURL(
    options?.baseURL || process.env.REACT_APP_ENDPOINT || getAPIBaseURL(),
  );

  const api = axios.create({
    method: 'post',
    headers: {
      Accept: 'application/json, text/csv, application/pdf',
      'Content-Type': 'application/json',
    },
    transformRequest: (data, headers) => {
      const requestPayload = {
        version:
          apiVersions?.[data.header.service]?.[data.header.function] ||
          process.env.REACT_APP_API_VERSION,
        build: process.env.REACT_APP_SENTRY_RELEASE,
      };

      if (data?.data instanceof FormData) {
        headers['Content-Type'] = 'multipart/form-data';

        data.data.append('service', data.header.service);
        data.data.append('function', data.header.function);
        data.data.append('clientID', data.header.clientID);
        data.data.append('sessionID', data.header.sessionID);
        data.data.append('subdomain', subdomain);

        Object.entries(requestPayload).forEach(([key, value]) =>
          data.data.append(key, value),
        );

        return data.data;
      } else {
        return JSON.stringify({
          ...data,
          subdomain,
          header: {
            ...data.header,
            ...requestPayload,
          },
        });
      }
    },
  });

  api.interceptors.response.use((response) => {
    const { header = {} } = response.data;

    const { errorNumber } = header;

    header?.warnings?.forEach((warning) => {
      warningHandler({ errorNumber, ...warning });
      if (
        warning.code === 'CORE_CODE_CLIENT_SESSION_UNAUTHORIZED_ACCESS' ||
        warning.code === 'CORE_CODE_CLIENT_SESSION_NOT_FOUND'
      ) {
        resetUserState();
      }
    });
    header?.errors?.forEach((error) => errorHandler({ errorNumber, ...error }));

    if (response.data.data?.loginStatus) {
      response.data.data.status = response.data.data.loginStatus;
    }

    return response;
  });

  return (
    api
      .post(
        `${baseURL}/${service}/${func}`,
        {
          header: {
            service: service.replace('framework/', ''),
            function: func,
            clientID: clientID || process.env.REACT_APP_TOKEN,
            sessionID: sessionID,
          },
          ...body,
          subdomain,
        },
        config,
      )
      .catch((error) => exceptionHandler(error)) || {}
  );
};

export default apiClient;

export const apiServiceClient = (service) => (func, data, options) =>
  apiClient(service, func, data, options);
